import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Leaf, Recycle, MapPin, Printer, CheckCircle2, ChevronRight, Factory, Sparkles, Info } from 'lucide-react';
import ModuleVisualization from './ModuleVisualization';
import PrinterAnimation from './PrinterAnimation';

const ManufacturingSection = () => {
  const [activeTab, setActiveTab] = useState('module');

  const sustainabilityFeatures = [
    {
      icon: <Leaf className="w-5 h-5" />,
      title: 'Plant-Based Plastics',
      description: 'PLA biopolymer derived from renewable crops like sugarcane and corn',
      highlight: 'Zero fossil fuel dependency'
    },
    {
      icon: <Printer className="w-5 h-5" />,
      title: '3D Printed Components',
      description: 'Precision manufacturing with near-zero material waste',
      highlight: 'On-demand production'
    },
    {
      icon: <Recycle className="w-5 h-5" />,
      title: 'Industrial Recyclability',
      description: 'Components designed for end-of-life recycling and circularity',
      highlight: 'Sustainable lifecycle'
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: 'Made in India',
      description: 'Fully assembled locally, supporting domestic manufacturing',
      highlight: 'Reduced carbon footprint'
    }
  ];

  const tabs = [
    { id: 'module', label: 'Module Design', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'process', label: 'Process', icon: <Factory className="w-4 h-4" /> },
    { id: 'materials', label: 'Materials', icon: <Leaf className="w-4 h-4" /> }
  ];

  return (
    <section className="py-24 px-6 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
            <Leaf className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">Sustainable Manufacturing</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            Thoughtfully Made.
            <br />
            <span className="text-emerald-400">Responsibly Built.</span>
          </h2>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            ClearWay is fully assembled in India using parts 3D printed from plant-based 
            and renewable plastics — engineering that cares for both people and planet.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex p-1 rounded-full bg-gray-800/50 border border-gray-700/50">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="min-h-[600px]">
          {/* Module Design Tab */}
          {activeTab === 'module' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Module Visualization */}
                <div className="order-2 lg:order-1">
                  <ModuleVisualization />
                </div>

                {/* Module Information */}
                <div className="order-1 lg:order-2 space-y-6">
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">Assistive Module Design</h3>
                    <p className="text-gray-400 leading-relaxed">
                      The ClearWay module is a compact electronic unit that mounts securely onto the walking stick shaft, 
                      providing environmental hazard detection through strategically positioned sensors.
                    </p>
                  </div>

                  {/* Sensor breakdown */}
                  <div className="space-y-4">
                    <div className="p-5 rounded-xl bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <div className="w-3 h-3 rounded-full bg-blue-400" />
                        </div>
                        <h4 className="font-medium">Ultrasonic Sensors</h4>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">
                        Two circular openings on the front face house ultrasonic transducers that emit sound waves to detect 
                        obstacles, walls, and objects in your walking path.
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Position: Front face</span>
                        <span>•</span>
                        <span>Detection: Forward obstacles</span>
                      </div>
                    </div>

                    <div className="p-5 rounded-xl bg-purple-500/10 border border-purple-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <div className="w-3 h-1.5 rounded bg-purple-400" />
                        </div>
                        <h4 className="font-medium">IR Ground Sensor</h4>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">
                        A rectangular opening on the bottom face houses a downward-facing IR sensor that scans the ground 
                        surface, detecting potholes, drops, steps, and sudden depth changes.
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Position: Bottom cutout</span>
                        <span>•</span>
                        <span>Detection: Ground hazards</span>
                      </div>
                    </div>

                    <div className="p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <div className="w-4 h-4 rounded-full border-2 border-emerald-400" />
                        </div>
                        <h4 className="font-medium">Mounting Collar</h4>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">
                        A cylindrical collar at the bottom of the module provides secure, non-rotating attachment to the 
                        walking stick shaft — ensuring stability during use.
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Position: Module base</span>
                        <span>•</span>
                        <span>Function: Secure mounting</span>
                      </div>
                    </div>
                  </div>

                  {/* Disclosure */}
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-gray-800/30 border border-gray-700/50">
                    <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-500">
                      <strong>Disclaimer:</strong> The visualization shown is representative only. Actual product design, 
                      dimensions, and appearance may vary from the rendered images.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Process Tab */}
          {activeTab === 'process' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <PrinterAnimation />
            </motion.div>
          )}

          {/* Materials Tab */}
          {activeTab === 'materials' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left - Features Grid */}
                <div className="grid sm:grid-cols-2 gap-6">
                  {sustainabilityFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="group p-6 rounded-2xl bg-gradient-to-b from-gray-800/40 to-gray-900/40 border border-gray-800/50 hover:border-emerald-500/30 transition-all"
                    >
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-4 group-hover:scale-110 transition-transform">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                      <p className="text-gray-500 text-sm mb-3">{feature.description}</p>
                      <div className="inline-flex items-center gap-1 text-xs text-emerald-400">
                        <CheckCircle2 className="w-3 h-3" />
                        {feature.highlight}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Right - Visual */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="relative"
                >
                  <div className="relative aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-500/10 via-transparent to-blue-500/10 border border-gray-800/50 p-8">
                    {/* Animated elements */}
                    <svg viewBox="0 0 400 400" className="w-full h-full">
                      {/* Central plant motif */}
                      <motion.g
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4, type: 'spring' }}
                      >
                        {/* Stem */}
                        <motion.path
                          d="M 200 350 Q 200 250 200 180"
                          stroke="#10b981"
                          strokeWidth="4"
                          fill="none"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ delay: 0.5, duration: 1 }}
                        />
                        {/* Leaves */}
                        <motion.path
                          d="M 200 250 Q 150 230 130 200 Q 160 220 200 250"
                          fill="#10b981"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.7 }}
                          transition={{ delay: 0.8 }}
                        />
                        <motion.path
                          d="M 200 220 Q 250 200 280 170 Q 250 200 200 220"
                          fill="#10b981"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.7 }}
                          transition={{ delay: 1 }}
                        />
                        <motion.path
                          d="M 200 190 Q 150 170 120 140 Q 150 160 200 190"
                          fill="#10b981"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.7 }}
                          transition={{ delay: 1.2 }}
                        />
                      </motion.g>

                      {/* Circular process indicators */}
                      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                        <motion.circle
                          key={i}
                          cx={200 + 130 * Math.cos((angle * Math.PI) / 180)}
                          cy={200 + 130 * Math.sin((angle * Math.PI) / 180)}
                          r="8"
                          fill="#10b981"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 0.5, scale: 1 }}
                          transition={{ delay: 0.3 + i * 0.15 }}
                        />
                      ))}

                      {/* Connecting arcs */}
                      <motion.circle
                        cx="200"
                        cy="200"
                        r="130"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="1"
                        strokeDasharray="10 20"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.3 }}
                        transition={{ delay: 0.5 }}
                      />
                      
                      {/* Inner glow ring */}
                      <motion.circle
                        cx="200"
                        cy="200"
                        r="60"
                        fill="none"
                        stroke="url(#plantGradient)"
                        strokeWidth="2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0.3, 0.6, 0.3] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      />

                      <defs>
                        <linearGradient id="plantGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                    </svg>

                    {/* Stats overlay */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="grid grid-cols-3 gap-4 p-4 rounded-xl bg-gray-900/80 backdrop-blur-sm border border-gray-700/50">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-400">100%</p>
                          <p className="text-xs text-gray-500">Plant-Based</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-400">~0</p>
                          <p className="text-xs text-gray-500">Material Waste</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-400">IN</p>
                          <p className="text-xs text-gray-500">Made in India</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <p className="text-gray-500 mb-4">Interested in our sustainable approach?</p>
          <a
            href="/about"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
          >
            Learn about our mission
            <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ManufacturingSection;
